import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Button, Spinner } from "reactstrap";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import InputGroup from "../common/input-group";
import { doNotSellInfoEmail } from "@/services/connectWithUsServices";
import useAlertBar from "@/hooks/useAlertBar";
import classes from "@/styles/personal-info-form/personal-info-form.module.css";

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  street: string;
  state: string;
  account?: string;
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email Address is required"),
  firstName: yup.string().required("First Name is required"),
  lastName: yup.string().required("Last Name is required"),
  phone: yup.string().required("Phone Number is required"),
  street: yup.string().required("Street Address is required"),
  state: yup.string().required("State / Region is required"),
});

const PersonalInfoFormMain = () => {
  const { openAlertBar } = useAlertBar();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema) as any,
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit: SubmitHandler<Inputs> = async (requestBody) => {
    try {
      setLoading(true);

      let { data } = await doNotSellInfoEmail(requestBody);

      setLoading(false);
      if (data.status) {
        openAlertBar("Your request has been received, Thank you!", true);
      } else {
        openAlertBar("Error while sending your request", false);
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
      openAlertBar("Error while sending your request", false);
    }
  };

  return (
    <main className="container">
      <section className="text-center my-5">
        <h1>Do Not Sell My Personal Information</h1>
      </section>
      <section className="mb-5">
        <div className="text-center">
          <p>
            We take the protection of your information seriously and are
            committed to ensuring the security of all customer data. New privacy
            regulations now require businesses like Shipvista.com to give
            consumers a process for opting out of any potential practice where
            customer personal information might be sold. Although we do not sell
            personal information for monetary value, our actions may be
            considered a “sale” under the CCPA.
          </p>
          <p>
            shipvista.com respects your right as a resident of the state of
            California. As such, When you use shipvista.com, you are trusting us
            with your information. we have provided an elective opt-out form to
            expedite your requests under the California Consumer Privacy Act
            (CCPA). You may also make this request by calling 1-855-434-3477.
            Learn more about our compliance by reading our privacy notice.
          </p>
          <p>
            Formally record your preference that shipvista.com does not sell
            your personal information by filling out the form.
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup
            required
            label="First Name"
            name="firstName"
            register={register}
            error={errors.firstName?.message}
          />
          <InputGroup
            required
            label="Last Name"
            name="lastName"
            register={register}
            error={errors.lastName?.message}
          />
          <InputGroup
            label="Company Name (optional)"
            name="companyName"
            register={register}
            error={errors.companyName?.message}
          />
          <InputGroup
            required
            label="Phone Number"
            name="phone"
            register={register}
            error={errors.phone?.message}
          />
          <InputGroup
            required
            label="Street Address"
            name="street"
            register={register}
            error={errors.street?.message}
          />
          <InputGroup
            required
            label="State / Region"
            name="state"
            register={register}
            error={errors.state?.message}
          />
          <InputGroup
            required
            label="Email"
            name="email"
            register={register}
            error={errors.email?.message}
          />
          <InputGroup
            label="Account No (optional)"
            name="account"
            register={register}
            error={errors.account?.message}
          />
          <p>
            <strong>Disclaimer:</strong> I agree that by submitting this form I
            am a resident of the State of California or I have been a resident
            of the state of California in the last 24 months.
          </p>
          <div className="text-center">
            <Button
              className={classes.submit}
              type="submit"
              color="primary"
              disabled={loading}
            >
              Submit
              {loading && <Spinner size="sm" className="ms-3" />}
            </Button>
          </div>
        </form>
      </section>
    </main>
  );
};

export default PersonalInfoFormMain;
